/**
 * MatBadges — badges d'état calculés pour un matériel
 *
 * Props:
 *   badges  : { badge_or, badge_loue, badge_non_conforme }
 *   size    : 'sm' (défaut) | 'md'
 *   showOk  : boolean — afficher le badge "Opérationnel" si aucun autre (défaut true)
 */
import React from 'react';

const BADGE_CFG = {
  urgent: {
    label: 'Urgent',
    bg: '#FEF2F2', border: '#FECACA', text: '#DC2626',
    dot: '#DC2626',
  },
  panne: {
    label: 'En panne',
    bg: '#FFF7ED', border: '#FED7AA', text: '#C2410C',
    dot: '#F97316',
  },
  entretien: {
    label: 'En entretien',
    bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8',
    dot: '#3B82F6',
  },
  garantie: {
    label: 'Garantie',
    bg: '#F5F3FF', border: '#DDD6FE', text: '#7C3AED',
    dot: '#8B5CF6',
  },
  loue: {
    label: 'Loué',
    bg: '#F0FDF4', border: '#BBF7D0', text: '#166534',
    dot: '#22C55E',
  },
  non_conforme: {
    label: 'Non conforme',
    bg: '#FEFCE8', border: '#FEF08A', text: '#92400E',
    dot: '#F59E0B',
  },
  reforme: {
    label: 'Réformé',
    bg: '#F8FAFC', border: '#CBD5E1', text: '#475569',
    dot: '#94A3B8',
  },
  operationnel: {
    label: 'Opérationnel',
    bg: '#F0FDF4', border: '#BBF7D0', text: '#166534',
    dot: '#22C55E',
  },
};

function Badge({ cfg, size = 'sm' }) {
  const pad    = size === 'md' ? '3px 10px' : '2px 7px';
  const fsize  = size === 'md' ? '12px' : '10px';
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        color: cfg.text, borderRadius: 20,
        padding: pad, fontSize: fsize, fontWeight: 600,
        whiteSpace: 'nowrap', lineHeight: 1.4,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

export default function MatBadges({ badges, size = 'sm', showOk = true }) {
  if (!badges) return null;

  const { badge_or, badge_loue, badge_non_conforme, badge_reforme } = badges;

  // Réformé est un état permanent exclusif — on n'affiche rien d'autre
  if (badge_reforme) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <Badge key="ref" cfg={BADGE_CFG.reforme} size={size} />
      </div>
    );
  }

  const items = [];
  if (badge_or) {
    const cfg = BADGE_CFG[badge_or];
    if (cfg) items.push(<Badge key="or" cfg={cfg} size={size} />);
  }
  if (badge_loue)          items.push(<Badge key="lou" cfg={BADGE_CFG.loue}            size={size} />);
  if (badge_non_conforme)  items.push(<Badge key="nc"  cfg={BADGE_CFG.non_conforme}    size={size} />);

  if (items.length === 0 && showOk) {
    items.push(<Badge key="ok" cfg={BADGE_CFG.operationnel} size={size} />);
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items}
    </div>
  );
}

/** Retourne le texte du badge principal (pour les tris/filtres) */
export function getBadgeLabel(badges) {
  if (!badges) return 'Opérationnel';
  if (badges.badge_reforme)            return 'Réformé';
  if (badges.badge_or === 'urgent')    return 'Urgent';
  if (badges.badge_or === 'panne')     return 'En panne';
  if (badges.badge_or === 'entretien') return 'En entretien';
  if (badges.badge_loue)               return 'Loué';
  if (badges.badge_non_conforme)       return 'Non conforme';
  return 'Opérationnel';
}
